import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import AnimatedSection from '../AnimatedSection';
import './SppbAutofill.css';

const CHROME_STORE_URL =
  'https://chromewebstore.google.com/detail/ez-rph-pbd-autofill/kplaehjahdaconmmaegdeiohidcdkloa';

const ChromeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#fff" />
    <circle cx="12" cy="12" r="4" fill="#4285F4" stroke="#fff" strokeWidth="1.2" />
    <path d="M12 2a10 10 0 0 1 8.66 5H12a5 5 0 0 0-4.33 2.5L4.34 4.7A9.98 9.98 0 0 1 12 2z" fill="#EA4335" />
    <path d="M22 12a10 10 0 0 1-10 10l4.33-8.5A5 5 0 0 0 17 12a5 5 0 0 0-.34-1.8h4.99c.23.9.35 1.84.35 2.8z" fill="#FBBC05" transform="rotate(120 12 12)" />
    <path d="M12 22A10 10 0 0 1 3.34 7l4.33 7.5A5 5 0 0 0 12 17c.63 0 1.23-.12 1.78-.33L9.45 21.7c.82.2 1.67.3 2.55.3z" fill="#34A853" transform="rotate(0 12 12)" />
  </svg>
);

const SppbAutofill: React.FC = () => {
  const { t } = useTranslation();

  const steps = [
    { key: 'record', image: '/images/feature-sppb-grid.png' },
    { key: 'scan', image: '/images/feature-sppb-cockpit-step.png' },
    { key: 'fill', image: '/images/extension-fill-simpan.jpg' },
  ];

  const trustItems = ['review', 'matched', 'simpan', 'password'];

  return (
    <section className="sppb-section" id="sppb">
      <div className="sppb-bg">
        <div className="sppb-glow" />
      </div>

      <div className="sppb-container">
        <AnimatedSection className="sppb-header">
          <span className="sppb-badge">
            <span className="sppb-badge-dot" />
            {t('sppb.badge')}
          </span>
          <h2 className="section-title">{t('sppb.title')}</h2>
          <p className="section-subtitle">{t('sppb.subtitle')}</p>
        </AnimatedSection>

        <div className="sppb-showcase">
          <motion.div
            className="sppb-showcase-main"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/images/extension-scan-compare.jpg"
              alt={t('sppb.mainImageAlt')}
              loading="lazy"
            />
          </motion.div>

          <motion.div
            className="sppb-showcase-chip"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <span className="sppb-chip-icon">✅</span>
            <span>{t('sppb.chip')}</span>
          </motion.div>
        </div>

        <div className="sppb-steps">
          {steps.map((step, index) => (
            <motion.div
              key={step.key}
              className="sppb-step-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -6 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
            >
              <div className="sppb-step-number">{index + 1}</div>
              <h3 className="sppb-step-title">{t(`sppb.steps.${step.key}.title`)}</h3>
              <p className="sppb-step-description">{t(`sppb.steps.${step.key}.description`)}</p>
              <div className="sppb-step-image">
                <img src={step.image} alt={t(`sppb.steps.${step.key}.title`)} loading="lazy" />
              </div>
            </motion.div>
          ))}
        </div>

        <AnimatedSection className="sppb-trust">
          <h3 className="sppb-trust-title">{t('sppb.trustTitle')}</h3>
          <div className="sppb-trust-grid">
            {trustItems.map((item) => (
              <div key={item} className="sppb-trust-item">
                <svg className="sppb-trust-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h4>{t(`sppb.trust.${item}.title`)}</h4>
                  <p>{t(`sppb.trust.${item}.description`)}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection className="sppb-cta">
          <motion.a
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-large sppb-cta-button"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
          >
            <ChromeIcon />
            {t('sppb.cta')}
          </motion.a>
          <p className="sppb-cta-note">{t('sppb.ctaNote')}</p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default SppbAutofill;
